import api from './api';

export interface Case {
  id: number;
  case_number: string;
  has_case_number?: boolean;  // ¿Tiene radicado? (SI/NO)
  internal_code?: string;
  process_type_id?: number;
  process_type_name?: string;
  current_stage_id?: number;
  client_name: string;
  client_id_number?: string;
  client_email?: string;
  client_phone?: string;
  opposing_party?: string;
  plaintiff?: string;  // Demandante
  defendant?: string;  // Demandado
  subject?: string;
  summary?: string;
  filing_date?: string;
  registration_date?: string;  // Fecha de registro en el sistema
  court_name?: string;
  court_id?: number | null;  // FK a courts (juzgado)
  jurisdiction_entity?: string;  // Jurisdicción/Entidad
  expediente_url?: string;  // Link del expediente
  judge_name?: string;
  status: 'active' | 'suspended' | 'closed';
  outcome?: string;
  closure_date?: string;
  semaphore_color: 'green' | 'yellow' | 'red';
  next_action_date?: string;
  next_action_description?: string;
  assigned_lawyer_id?: number;  // Abogado encargado
  assigned_lawyer_name?: string;
  last_action_notes?: string;  // Observación general - Última actuación
  next_hearing_date?: string;  // Fecha próxima audiencia
  next_hearing_time?: string;  // Hora próxima audiencia
  hearing_rescheduled?: boolean;  // Se fijó nueva fecha
  rescheduled_hearing_date?: string;  // Nueva fecha de audiencia
  process_status?: string;  // Estado del proceso
  // FNG / FAG
  has_fng_fag?: boolean;
  fng_fag_type?: 'FNG' | 'FAG' | 'AMBOS' | null;
  fng_fag_docs_sent?: boolean | null;
  fng_fag_docs_sent_at?: string | null;
  // Facturación
  billing_criteria?: string;
  billing_trigger?: 'AL_PRESENTAR' | 'AL_NOTIFICAR' | 'AL_OBTENER_SENTENCIA' | 'MENSUAL' | 'POR_ETAPA' | 'OTRO' | null;
  // Cartera
  portfolio_id?: number | null;
  portfolio_name?: string | null;
  // Alertas
  last_significant_action_at?: string;
  sentence_deadline_at?: string;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CaseListResponse {
  data: Case[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CaseListParams {
  page?: number;
  limit?: number;
  status?: string;
  semaphore_color?: string;
  search?: string;
  portfolio_id?: number;
  lawyer_id?: number;
}

export interface CreateCaseDto {
  case_number?: string | null;
  has_case_number?: boolean;
  client_name: string;
  process_type_id?: number | null;
  client_email?: string;
  client_phone?: string;
  plaintiff?: string;
  defendant?: string;
  court_name?: string;
  court_id?: number | null;
  jurisdiction_entity?: string;
  expediente_url?: string | null;
  status?: string;
  filing_date?: string | null;
  registration_date?: string | null;
  next_action_date?: string | null;
  next_action_description?: string;
  assigned_lawyer_id?: number | null;
  last_action_notes?: string;
  next_hearing_date?: string | null;
  next_hearing_time?: string | null;
  hearing_rescheduled?: boolean;
  rescheduled_hearing_date?: string | null;
  process_status?: string;
  has_fng_fag?: boolean;
  fng_fag_type?: 'FNG' | 'FAG' | 'AMBOS' | null;
  fng_fag_docs_sent?: boolean | null;
  fng_fag_docs_sent_at?: string | null;
  billing_criteria?: string;
  billing_trigger?: 'AL_PRESENTAR' | 'AL_NOTIFICAR' | 'AL_OBTENER_SENTENCIA' | 'MENSUAL' | 'POR_ETAPA' | 'OTRO' | null;
  portfolio_id?: number | null;
}

const casesService = {
  async list(params?: CaseListParams): Promise<CaseListResponse> {
    const response = await api.get('/cases', { params });
    // Handle different API response formats
    const responseData = response.data.data || response.data;
    if (Array.isArray(responseData)) {
      return {
        data: responseData,
        pagination: {
          page: 1,
          limit: responseData.length,
          total: responseData.length,
          totalPages: 1
        }
      };
    }
    return {
      data: responseData.cases || responseData.data || [],
      pagination: responseData.pagination || {
        page: 1,
        limit: 10,
        total: responseData.total || 0,
        totalPages: Math.ceil((responseData.total || 0) / 10)
      }
    };
  },

  async getById(id: number): Promise<Case> {
    const response = await api.get(`/cases/${id}`);
    return response.data.data || response.data;
  },

  async create(data: CreateCaseDto): Promise<Case> {
    const response = await api.post('/cases', data);
    return response.data.data || response.data;
  },

  async update(id: number, data: Partial<CreateCaseDto>): Promise<Case> {
    const response = await api.patch(`/cases/${id}`, data);
    return response.data.data || response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/cases/${id}`);
  },
};

export default casesService;
